import { useState, useEffect, useCallback, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';

const LOG = (...args) => console.log('[useSuggestions]', ...args);

/**
 * useSuggestions — centralized suggestion (propose modification) logic.
 *
 * Marks in the document always keep their original tempId (never renamed).
 * State tracks permanentId. The `markIdMap` ref maps permanentId → tempId
 * so mark operations can find the right marks.
 */
export function useSuggestions({ editor, socket, contractId, currentUser }) {
  const [suggestions, setSuggestions] = useState([]);
  const [suggestMode, setSuggestModeState] = useState(false);
  const [activeSuggestionId, setActiveSuggestionId] = useState(null);
  const [pendingSuggestion, setPendingSuggestionState] = useState(null);
  const isMounted = useRef(true);
  const pendingTempIds = useRef(new Set());
  const undoTrash = useRef(new Map());
  // Maps permanentId → tempId (the ID actually in the document marks)
  const markIdMap = useRef(new Map());

  // Helper: get the mark ID in the document for a given suggestion ID (permanentId → tempId)
  // Follows the chain: newPermId → oldPermId → tempId
  const getMarkId = (suggestionId) => {
    let id = suggestionId;
    while (markIdMap.current.has(id)) {
      id = markIdMap.current.get(id);
    }
    return id;
  };

  // Helper: reverse lookup — get the permanent ID for a mark ID (tempId → permanentId)
  const resolveMarkId = useCallback((markId) => {
    for (const [permId, tempId] of markIdMap.current.entries()) {
      if (tempId === String(markId)) return permId;
    }
    return markId;
  }, []);

  useEffect(() => {
    isMounted.current = true;
    return () => { isMounted.current = false; };
  }, []);

  // ── Socket event handling ─────────────────────────────────────────────────

  useEffect(() => {
    if (!socket || !editor) return;

    const handleSuggestionEvent = (data) => {
      if (!isMounted.current) return;
      const { type } = data;
      LOG('socket event:', type, data);

      if (type === 'sync') {
        const synced = data.suggestions || [];
        // The DOM marks (Yjs-persisted) carry the data-sid that was
        // assigned the LAST time renameSuggestion ran in some session — it
        // may differ from the permanent_id we just received from the DB
        // (different sessions, Yjs/DB drift). Walk the editor doc and try to
        // match each synced suggestion to a mark by content; populate
        // markIdMap so getMarkId() can resolve permanent_id → DOM mark id
        // and the position lookup, accept/reject, scroll-to all work after reload.
        if (editor) {
          const norm = (s) => (s || '')
            .replace(/\s+/g, ' ')
            .replace(/[‘’]/g, "'")
            .replace(/[“”]/g, '"')
            .replace(/[–—]/g, '-')
            .trim()
            .toLowerCase();

          // Collect text per mark id (delete-side gives us the originalText)
          const delTextBySid = new Map();
          editor.state.doc.descendants((node) => {
            if (!node.isText) return;
            node.marks.forEach((m) => {
              if (m.type.name !== 'suggestionDelete') return;
              const sid = m.attrs?.suggestionId;
              if (!sid || sid === 'pending') return;
              delTextBySid.set(String(sid), (delTextBySid.get(String(sid)) || '') + node.text);
            });
          });

          // Index by normalized prefix for fast lookup
          const sidByPrefix = new Map();
          delTextBySid.forEach((txt, sid) => {
            const key = norm(txt).slice(0, 50);
            if (key && !sidByPrefix.has(key)) sidByPrefix.set(key, sid);
          });

          synced.forEach((s) => {
            if (!s.id || !s.originalText) return;
            // If markIdMap already maps this perm id, skip
            if (markIdMap.current.has(s.id)) return;
            const key = norm(s.originalText).slice(0, 50);
            if (!key) return;
            const matchedSid = sidByPrefix.get(key);
            if (matchedSid && matchedSid !== s.id) {
              markIdMap.current.set(s.id, matchedSid);
              LOG('sync: mapped perm', s.id, '→ mark', matchedSid);
            }
          });
        }
        setSuggestions(synced);
      } else if (type === 'add') {
        const s = data.suggestion;
        // Server broadcasts temp_id alongside the permanent suggestion.id
        // so remote clients can map permanentId → tempId (mark in doc uses tempId via Yjs)
        if (data.temp_id && data.temp_id !== s.id && !markIdMap.current.has(s.id)) {
          markIdMap.current.set(s.id, data.temp_id);
          LOG('remote add: mapped', s.id, '→', data.temp_id);
        }
        setSuggestions(prev => {
          const exists = prev.find(x => x.id === s.id);
          if (exists) return prev.map(x => x.id === s.id ? s : x);
          return [s, ...prev];
        });
      } else if (type === 'accepted') {
        const { suggestionId } = data;
        setSuggestions(prev =>
          prev.map(s => s.id === suggestionId ? { ...s, status: 'accepted' } : s)
        );
        if (editor) editor.commands.acceptSuggestion(getMarkId(suggestionId));
      } else if (type === 'rejected') {
        const { suggestionId } = data;
        setSuggestions(prev =>
          prev.map(s => s.id === suggestionId ? { ...s, status: 'rejected' } : s)
        );
        if (editor) editor.commands.rejectSuggestion(getMarkId(suggestionId));
      } else if (type === 'delete') {
        const { suggestionId } = data;
        // If the marks are still in the doc (redo restored them), ignore the stale delete
        const mid = getMarkId(suggestionId);
        let markExists = false;
        if (editor) {
          editor.state.doc.descendants((node) => {
            if (!node.isText || markExists) return;
            node.marks.forEach(mark => {
              if ((mark.type.name === 'suggestionDelete' || mark.type.name === 'suggestionInsert') &&
                  String(mark.attrs.suggestionId) === String(mid)) markExists = true;
            });
          });
        }
        if (markExists) {
          LOG('socket delete IGNORED (mark still in doc):', suggestionId);
          return;
        }
        setSuggestions(prev => prev.filter(s => s.id !== suggestionId));
        if (editor) editor.commands.rejectSuggestion(mid);
      }
    };

    const handleAck = (data) => {
      if (!isMounted.current || !data.temp_id || !data.permanent_id) return;
      LOG('ack:', data.temp_id, '→', data.permanent_id);
      pendingTempIds.current.delete(data.temp_id);
      markIdMap.current.set(data.permanent_id, data.temp_id);

      // Check if the mark still exists (undo may have removed it before ack arrived)
      let markExists = false;
      if (editor) {
        editor.state.doc.descendants((node) => {
          if (!node.isText || markExists) return;
          node.marks.forEach(mark => {
            if ((mark.type.name === 'suggestionDelete' || mark.type.name === 'suggestionInsert') &&
                String(mark.attrs.suggestionId) === data.temp_id) markExists = true;
          });
        });
      }
      if (!markExists) {
        LOG('ack: mark already gone (undone), saving to undoTrash:', data.permanent_id);
        setSuggestions(prev => {
          const entry = prev.find(s => s.id === data.temp_id || s.id === data.permanent_id);
          if (entry) {
            undoTrash.current.set(data.permanent_id, { ...entry, id: data.permanent_id });
          }
          return prev.filter(s => s.id !== data.temp_id && s.id !== data.permanent_id);
        });
        return;
      }

      setSuggestions(prev => {
        const hasPermanent = prev.some(s => s.id === data.permanent_id);
        if (hasPermanent) {
          return prev.filter(s => s.id !== data.temp_id);
        }
        return prev.map(s => s.id === data.temp_id ? { ...s, id: data.permanent_id } : s);
      });
    };

    socket.on('suggestion', handleSuggestionEvent);
    socket.on('suggestion_ack', handleAck);

    socket.emit('suggestion', { type: 'request_sync', contract_id: contractId });

    return () => {
      socket.off('suggestion', handleSuggestionEvent);
      socket.off('suggestion_ack', handleAck);
    };
  }, [socket, editor, contractId]);

  // ── Reconcile hook state with editor marks on undo/redo ──────────────────
  const reconcile = useCallback(() => {
    if (!editor) return;

    LOG('reconcile: START');

    // Collect all suggestion mark IDs in the document (these are tempIds)
    const docMarkIds = new Set();
    editor.state.doc.descendants((node) => {
      if (!node.isText) return;
      node.marks.forEach(mark => {
        const id = mark.attrs?.suggestionId;
        if (id && id !== 'pending') docMarkIds.add(String(id));
      });
    });

    LOG('reconcile: marks in doc:', [...docMarkIds]);
    LOG('reconcile: markIdMap:', [...markIdMap.current.entries()]);

    const orphanedIds = [];
    const restoredSuggestions = [];

    setSuggestions(prev => {
      LOG('reconcile: suggestions in state:', prev.map(s => ({ id: s.id, status: s.status })));
      let changed = false;

      // Build reverse map: tempId → permanentId
      const tempToPermanent = new Map();
      markIdMap.current.forEach((tempId, permId) => {
        tempToPermanent.set(tempId, permId);
      });

      // Index state by markId
      const stateMarkIds = new Set();
      prev.forEach(s => {
        const mid = String(markIdMap.current.get(s.id) || s.id);
        stateMarkIds.add(mid);
      });

      // Detect marks in doc with no state entry (redo restored marks)
      docMarkIds.forEach(markId => {
        if (stateMarkIds.has(markId)) return;
        if (pendingTempIds.current.has(markId)) return;

        const permanentId = tempToPermanent.get(markId);
        const trashKey = permanentId || markId;
        const trashed = undoTrash.current.get(trashKey);
        if (trashed) {
          LOG('reconcile: RESTORE from undoTrash:', trashKey, '(markId:', markId, ')');
          changed = true;
          restoredSuggestions.push(trashed);
          undoTrash.current.delete(trashKey);
        }
      });

      const next = [
        ...restoredSuggestions,
        ...prev.filter(s => {
          if (pendingTempIds.current.has(s.id)) return true;

          const mid = String(markIdMap.current.get(s.id) || s.id);
          if (docMarkIds.has(mid)) return true;

          if (s.status === 'pending') {
            LOG('reconcile: ORPHANED:', s.id, '(markId:', mid, ')');
            changed = true;
            orphanedIds.push(s.id);
            undoTrash.current.set(s.id, s);
            return false;
          }
          return true;
        }).map(s => {
          const mid = String(markIdMap.current.get(s.id) || s.id);
          if (s.status !== 'pending' && docMarkIds.has(mid)) {
            LOG('reconcile: REVERT to pending:', s.id);
            changed = true;
            return { ...s, status: 'pending' };
          }
          return s;
        })
      ];
      LOG('reconcile: changed=', changed, 'orphaned=', orphanedIds, 'restored=', restoredSuggestions.map(s => s.id));
      return changed ? next : prev;
    });

    orphanedIds.forEach(id => {
      LOG('reconcile: emitting delete:', id);
      socket?.emit('suggestion', {
        type: 'delete',
        contract_id: contractId,
        suggestionId: id,
      });
    });

    restoredSuggestions.forEach(s => {
      LOG('reconcile: emitting re-add:', s.id);
      pendingTempIds.current.add(s.id);
      socket?.emit('suggestion', {
        type: 'add',
        contract_id: contractId,
        suggestion: {
          id: s.id,
          originalText: s.originalText,
          suggestedText: s.suggestedText,
          contextText: s.contextText || '',
        },
      });
    });

    LOG('reconcile: END');
  }, [editor, socket, contractId]);

  // Trigger reconciliation on keyboard undo/redo
  useEffect(() => {
    if (!editor) return;

    const handleKeyDown = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'z') {
        LOG('keydown detected:', event.shiftKey ? 'REDO' : 'UNDO');
        setTimeout(reconcile, 50);
      }
    };

    const dom = editor.view.dom;
    dom.addEventListener('keydown', handleKeyDown);
    return () => dom.removeEventListener('keydown', handleKeyDown);
  }, [editor, reconcile]);

  // ── Actions ───────────────────────────────────────────────────────────────

  const setSuggestMode = useCallback((enabled) => {
    setSuggestModeState(enabled);
    if (editor) editor.commands.setSuggestMode(enabled);
  }, [editor]);

  const setPendingSuggestion = useCallback((pendingData) => {
    LOG('setPendingSuggestion:', pendingData ? { from: pendingData.from, to: pendingData.to } : null);
    setPendingSuggestionState(pendingData);
    if (!editor) return;
    // Pending highlight is UI-only — must not enter the undo stack
    const { tr } = editor.state;
    const deleteMarkType = editor.state.schema.marks.suggestionDelete;
    editor.state.doc.descendants((node, pos) => {
      if (!node.isText) return;
      node.marks.forEach(mark => {
        if (mark.type === deleteMarkType && mark.attrs.suggestionId === 'pending') {
          tr.removeMark(pos, pos + node.nodeSize, deleteMarkType);
        }
      });
    });
    if (pendingData && pendingData.from !== undefined && pendingData.to !== undefined) {
      tr.addMark(pendingData.from, pendingData.to, deleteMarkType.create({ suggestionId: 'pending' }));
    }
    tr.setMeta('addToHistory', false);
    editor.view.dispatch(tr);
  }, [editor]);

  const addSuggestion = useCallback((suggestedText, pendingData) => {
    if (!editor || !socket || !currentUser) return;

    const originalText = typeof pendingData === 'object'
      ? pendingData?.originalText || ''
      : pendingData || '';
    const from = typeof pendingData === 'object' ? pendingData?.from : undefined;
    const to   = typeof pendingData === 'object' ? pendingData?.to   : undefined;

    const tempId = uuidv4();
    pendingTempIds.current.add(tempId);
    const now = new Date().toISOString();

    LOG('addSuggestion: tempId=', tempId, 'from=', from, 'to=', to);

    if (from !== undefined && to !== undefined) {
      // Remove pending marks (non-undoable)
      {
        const { tr } = editor.state;
        const deleteMarkType = editor.state.schema.marks.suggestionDelete;
        let hasPending = false;
        editor.state.doc.descendants((node, pos) => {
          if (!node.isText) return;
          node.marks.forEach(mark => {
            if (mark.type === deleteMarkType && mark.attrs.suggestionId === 'pending') {
              tr.removeMark(pos, pos + node.nodeSize, deleteMarkType);
              hasPending = true;
            }
          });
        });
        if (hasPending) {
          tr.setMeta('addToHistory', false);
          editor.view.dispatch(tr);
        }
      }
      // Add suggestion marks (this IS undoable)
      editor.commands.addSuggestion({ id: tempId, from, to, suggestedText });
      editor.commands.setTextSelection(from);
      // Mark added synchronously — safe to remove from pendingTempIds
      // so reconcile can detect undo removal
      pendingTempIds.current.delete(tempId);
    }

    const newSuggestion = {
      id: tempId,
      originalText,
      suggestedText,
      contextText: '',
      status: 'pending',
      timestamp: now,
      user: {
        id: currentUser.id,
        first_name: currentUser.first_name,
        last_name: currentUser.last_name,
        email: currentUser.email,
      },
    };

    setSuggestions(prev => [newSuggestion, ...prev]);

    socket.emit('suggestion', {
      type: 'add',
      contract_id: contractId,
      suggestion: { id: tempId, originalText, suggestedText, contextText: '' },
    });

    setPendingSuggestionState(null);
    return tempId;
  }, [editor, socket, currentUser, contractId]);

  const acceptSuggestion = useCallback((suggestionId) => {
    if (!socket || !editor) return;
    LOG('acceptSuggestion:', suggestionId, 'markId:', getMarkId(suggestionId));
    setSuggestions(prev =>
      prev.map(s => s.id === suggestionId ? { ...s, status: 'accepted' } : s)
    );
    editor.commands.acceptSuggestion(getMarkId(suggestionId));
    socket.emit('suggestion', {
      type: 'accept',
      contract_id: contractId,
      suggestionId,
    });
  }, [socket, editor, contractId]);

  const rejectSuggestion = useCallback((suggestionId) => {
    if (!socket || !editor) return;
    LOG('rejectSuggestion:', suggestionId, 'markId:', getMarkId(suggestionId));
    setSuggestions(prev =>
      prev.map(s => s.id === suggestionId ? { ...s, status: 'rejected' } : s)
    );
    editor.commands.rejectSuggestion(getMarkId(suggestionId));
    socket.emit('suggestion', {
      type: 'reject',
      contract_id: contractId,
      suggestionId,
    });
  }, [socket, editor, contractId]);

  const deleteSuggestion = useCallback((suggestionId) => {
    if (!socket || !editor) return;
    LOG('deleteSuggestion:', suggestionId, 'markId:', getMarkId(suggestionId));
    setSuggestions(prev => prev.filter(s => s.id !== suggestionId));
    editor.commands.rejectSuggestion(getMarkId(suggestionId));
    socket.emit('suggestion', {
      type: 'delete',
      contract_id: contractId,
      suggestionId,
    });
  }, [socket, editor, contractId]);

  const pendingSuggestions = suggestions.filter(s => s.status === 'pending');

  // Add a suggestion that was created externally (e.g., by the AI backend).
  // Only updates local state — marks are applied separately by the caller.
  const addExternalSuggestion = useCallback((suggestion) => {
    setSuggestions(prev => {
      if (prev.find(s => s.id === suggestion.id)) return prev;
      return [{
        id: suggestion.id,
        originalText: suggestion.originalText || '',
        suggestedText: suggestion.suggestedText || '',
        contextText: suggestion.contextText || '',
        status: suggestion.status || 'pending',
        is_system: suggestion.is_system || false,
        timestamp: suggestion.timestamp || new Date().toISOString(),
        user: suggestion.user || {},
      }, ...prev];
    });
  }, []);

  return {
    suggestions,
    pendingSuggestions,
    suggestMode,
    activeSuggestionId,
    pendingSuggestion,
    setSuggestMode,
    setActiveSuggestionId,
    setPendingSuggestion,
    addSuggestion,
    addExternalSuggestion,
    acceptSuggestion,
    rejectSuggestion,
    deleteSuggestion,
    reconcileAfterUndoRedo: reconcile,
    resolveMarkId,
    getMarkId,
  };
}
